// Document Intelligence - AI Document Analyst for Healthcare Operations
// Upload, analyze, and chat about documents with AI-powered insights

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  FileText, Upload, Send, Loader2, Bot, User, Download,
  FileSpreadsheet, File, FileType, Sparkles, History,
  Search, Trash2, ChevronRight, CheckCircle, AlertCircle,
  Clock, RefreshCw, Clipboard, ExternalLink, Shield,
  DollarSign, Users, Activity, Heart, BarChart3, X,
  MessageSquare, Wand2, Target, ArrowRight, Plus, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick analysis templates
const ANALYSIS_TEMPLATES = [
  { id: 'financial', icon: DollarSign, label: 'Financial Analysis', color: 'text-emerald-400', description: 'Revenue, costs, margins, budget variance' },
  { id: 'compliance', icon: Shield, label: 'Compliance Review', color: 'text-blue-400', description: 'CMS, CHAP, regulatory issues' },
  { id: 'staffing', icon: Users, label: 'Staffing Analysis', color: 'text-purple-400', description: 'Workforce, caseloads, productivity' },
  { id: 'quality', icon: Heart, label: 'Quality Metrics', color: 'text-red-400', description: 'CAHPS, HIS, clinical outcomes' },
  { id: 'executive', icon: BarChart3, label: 'Executive Summary', color: 'text-amber-400', description: 'Leadership-ready overview' }
];

// Contextual instruction chips based on document type
const INSTRUCTION_CHIPS = {
  default: [
    "Summarize key findings in 3 bullets",
    "What are the biggest risks?",
    "Extract all action items with deadlines",
    "Rewrite this for the board of directors",
    "Compare against industry benchmarks",
    "What data is missing?",
    "Focus only on compliance issues",
    "Challenge weak conclusions"
  ],
  financial_report: [
    "Break down revenue by service line",
    "What's driving cost increases?",
    "Compare margins to hospice benchmarks",
    "Identify top 3 budget variances",
    "Project next quarter based on trends"
  ],
  compliance_audit: [
    "List all deficiencies by severity",
    "What's the corrective action timeline?",
    "Map findings to CMS CoP sections",
    "What needs immediate attention?",
    "Compare to last audit results"
  ],
  staffing_report: [
    "Who is at highest burnout risk?",
    "Calculate FTE gaps by discipline",
    "Show caseload distribution",
    "Identify overtime patterns",
    "What hiring is needed?"
  ],
  clinical_report: [
    "Summarize patient outcomes",
    "Flag any quality concerns",
    "What interventions are needed?",
    "Compare to CAHPS benchmarks",
    "Identify trends in symptom management"
  ]
};

// Simple markdown renderer
function renderMarkdown(text) {
  if (!text) return null;
  
  const lines = text.split('\n');
  const elements = [];
  let inList = false;
  let listItems = [];
  let inTable = false;
  let tableRows = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 my-2 text-slate-300">
          {listItems.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
        </ul>
      );
      listItems = [];
      inList = false;
    }
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      const headerRow = tableRows[0];
      const dataRows = tableRows.slice(1).filter(r => !r.match(/^\|[\s-|]+\|$/));
      elements.push(
        <div key={`table-${elements.length}`} className="overflow-x-auto my-3">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-600">
                {headerRow.split('|').filter(c => c.trim()).map((cell, i) => (
                  <th key={i} className="text-left px-3 py-2 text-slate-400 font-medium">{cell.trim()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, ri) => (
                <tr key={ri} className="border-b border-slate-700/50">
                  {row.split('|').filter(c => c.trim()).map((cell, ci) => (
                    <td key={ci} className="px-3 py-1.5 text-slate-300">{cell.trim()}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
      inTable = false;
    }
  };

  const renderInline = (text) => {
    // Bold
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Table detection
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      flushList();
      inTable = true;
      tableRows.push(trimmed);
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Headers
    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h3 key={`h-${i}`} className="text-lg font-bold text-white mt-4 mb-2 border-b border-slate-700 pb-1">
          {renderInline(trimmed.slice(3))}
        </h3>
      );
      continue;
    }
    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h4 key={`h-${i}`} className="text-base font-semibold text-slate-200 mt-3 mb-1">
          {renderInline(trimmed.slice(4))}
        </h4>
      );
      continue;
    }
    if (trimmed.startsWith('# ')) {
      flushList();
      elements.push(
        <h2 key={`h-${i}`} className="text-xl font-bold text-white mt-4 mb-2">
          {renderInline(trimmed.slice(2))}
        </h2>
      );
      continue;
    }

    // List items
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      inList = true;
      listItems.push(trimmed.slice(2));
      continue;
    }
    if (/^\d+\.\s/.test(trimmed)) {
      inList = true;
      listItems.push(trimmed.replace(/^\d+\.\s/, ''));
      continue;
    }

    flushList();

    // Empty lines
    if (!trimmed) {
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={`p-${i}`} className="text-slate-300 text-sm leading-relaxed my-1">
        {renderInline(trimmed)}
      </p>
    );
  }

  flushList();
  flushTable();

  return elements;
}

export default function DocumentIntelligence() {
  const { token } = useAuth();
  
  // File upload state
  const [file, setFile] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Analysis state
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // History state
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // UI state
  const [activeView, setActiveView] = useState('upload'); // 'upload' | 'chat' | 'history'
  const [showTextInput, setShowTextInput] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  const headers = { 'Authorization': `Bearer ${token}` };

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [chatMessages]);

  // Focus chat input when switching to chat view
  useEffect(() => {
    if (activeView === 'chat') {
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  }, [activeView]);

  // Fetch analysis history
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/document-intelligence/history?limit=20`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAnalysisHistory(data.analyses || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Drag & drop handlers
  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === "dragenter" || e.type === "dragover"); };
  const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); if (e.dataTransfer.files?.[0]) { setFile(e.dataTransfer.files[0]); setTextContent(''); }};
  const handleFileSelect = (e) => { if (e.target.files?.[0]) { setFile(e.target.files[0]); setTextContent(''); }};

  // Get contextual chips based on document type
  const getInstructionChips = () => {
    const docType = currentAnalysis?.document_overview?.type?.type || 'default';
    const typeChips = INSTRUCTION_CHIPS[docType] || [];
    const defaultChips = INSTRUCTION_CHIPS.default;
    // Merge type-specific + defaults, deduplicate
    const all = [...typeChips, ...defaultChips];
    return [...new Set(all)].slice(0, 8);
  };

  // Analyze document
  const analyzeDocument = async (template = null) => {
    if (!file && !textContent.trim()) {
      toast.error('Please upload a file or paste text content');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      if (file) formData.append('file', file);
      else formData.append('text_content', textContent);
      
      // Build user query from custom instructions + template
      let userQuery = '';
      if (customInstructions.trim()) userQuery = customInstructions.trim();
      if (template) userQuery = (userQuery ? userQuery + '. Also: ' : '') + `Analyze with ${template} focus`;
      if (userQuery) formData.append('user_query', userQuery);

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const endpoint = template 
        ? `${API_URL}/api/document-intelligence/quick-analyze/${template}`
        : `${API_URL}/api/document-intelligence/analyze`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const data = await response.json();
        setCurrentAnalysis(data.analysis);
        setSessionId(data.analysis.session_id);
        
        // Build initial messages
        const initialMessages = [];
        if (customInstructions.trim()) {
          initialMessages.push({
            role: 'user',
            content: customInstructions.trim(),
            timestamp: new Date().toISOString(),
            isInstruction: true
          });
        }
        initialMessages.push({
          role: 'assistant',
          content: data.analysis.ai_analysis,
          timestamp: new Date().toISOString()
        });
        
        setChatMessages(initialMessages);
        setActiveView('chat');
        toast.success('Document analyzed! Chat with the AI about your document.');
        fetchHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Analysis failed');
      }
    } catch (error) {
      toast.error('Failed to analyze document');
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Send follow-up message
  const sendFollowUp = async (message = chatInput) => {
    if (!message.trim() || !sessionId) return;

    setChatLoading(true);
    setChatMessages(prev => [...prev, { role: 'user', content: message, timestamp: new Date().toISOString() }]);
    setChatInput('');

    try {
      const response = await fetch(`${API_URL}/api/document-intelligence/follow-up`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, query: message })
      });

      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: data.timestamp }]);
      } else {
        toast.error('Failed to process follow-up');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setChatLoading(false);
    }
  };

  // Export functions
  const exportCSV = async () => {
    if (!currentAnalysis?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/document-intelligence/export/csv/${currentAnalysis.id}`, { method: 'POST', headers });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = data.filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('CSV exported');
      }
    } catch { toast.error('Export failed'); }
  };

  const exportPDF = async () => {
    if (!currentAnalysis?.id) return;
    try {
      const response = await fetch(`${API_URL}/api/document-intelligence/export/pdf/${currentAnalysis.id}`, { method: 'POST', headers });
      if (response.ok) {
        const data = await response.json();
        const c = data.content;
        const metricsHtml = c.document_overview?.metrics ? Object.entries(c.document_overview.metrics).map(([key, values]) => `<tr><td>${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td><td>${Array.isArray(values) ? values.slice(0,5).join(', ') : values}</td></tr>`).join('') : '';
        const formattedAnalysis = c.analysis.replace(/^## (.+)$/gm, '<h2>$1</h2>').replace(/^### (.+)$/gm, '<h3>$1</h3>').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/^\- (.+)$/gm, '<li>$1</li>').replace(/\n\n/g, '</p><p>').replace(/\|/g, ' | ');
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`<!DOCTYPE html><html><head><title>${c.title}</title><style>@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}*{box-sizing:border-box;}body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;padding:40px;max-width:900px;margin:0 auto;color:#1f2937;line-height:1.6;}.header{background:linear-gradient(135deg,#0f172a 0%,#1e3a5f 100%);color:white;padding:30px;margin:-40px -40px 30px -40px;border-radius:0 0 12px 12px;}.header h1{margin:0 0 5px 0;font-size:28px;}.header .subtitle{color:#94a3b8;font-size:14px;margin:0;}.meta-bar{display:flex;gap:20px;background:#f8fafc;padding:15px 20px;border-radius:8px;margin-bottom:25px;border-left:4px solid #10b981;}.meta-item{display:flex;flex-direction:column;}.meta-label{font-size:11px;color:#6b7280;text-transform:uppercase;}.meta-value{font-size:14px;font-weight:600;color:#1f2937;}h2{color:#0f172a;margin-top:30px;padding-bottom:8px;border-bottom:2px solid #10b981;font-size:20px;}h3{color:#374151;margin-top:20px;font-size:16px;}.analysis-content{background:#ffffff;padding:20px;border:1px solid #e5e7eb;border-radius:8px;white-space:pre-wrap;font-size:14px;}table{width:100%;border-collapse:collapse;margin:20px 0;font-size:13px;}th,td{border:1px solid #e5e7eb;padding:12px;text-align:left;}th{background:#f8fafc;font-weight:600;color:#374151;}tr:nth-child(even){background:#f9fafb;}.footer{margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb;text-align:center;color:#9ca3af;font-size:12px;}.badge{display:inline-block;background:#10b981;color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;}</style></head><body><div class="header"><h1>${c.title}</h1><p class="subtitle">${c.subtitle||''}</p></div><div class="meta-bar"><div class="meta-item"><span class="meta-label">Document</span><span class="meta-value">${c.document_overview?.filename||'N/A'}</span></div><div class="meta-item"><span class="meta-label">Type</span><span class="meta-value"><span class="badge">${c.document_overview?.type||'Unknown'}</span></span></div><div class="meta-item"><span class="meta-label">Confidence</span><span class="meta-value">${c.document_overview?.confidence||'N/A'}</span></div><div class="meta-item"><span class="meta-label">Generated</span><span class="meta-value">${c.generated_at}</span></div></div>${metricsHtml?`<h2>Extracted Metrics</h2><table><tr><th>Category</th><th>Values Found</th></tr>${metricsHtml}</table>`:''}<h2>AI Analysis</h2><div class="analysis-content">${formattedAnalysis}</div><div class="footer">${c.footer||'Generated by CareFlow Intelligence'}</div></body></html>`);
        printWindow.document.close();
        printWindow.print();
        toast.success('PDF ready');
      }
    } catch { toast.error('Export failed'); }
  };

  // Load previous analysis
  const loadAnalysis = async (analysisId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/document-intelligence/analysis/${analysisId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setCurrentAnalysis(data.analysis);
        setSessionId(data.analysis.session_id);
        
        const convResponse = await fetch(`${API_URL}/api/document-intelligence/conversation/${data.analysis.session_id}`, { headers });
        const messages = [{ role: 'assistant', content: data.analysis.ai_analysis, timestamp: data.analysis.timestamp }];
        
        if (convResponse.ok) {
          const convData = await convResponse.json();
          if (convData.messages?.length > 0) {
            messages.push(...convData.messages.map(m => ({ role: m.role, content: m.content, timestamp: m.timestamp })));
          }
        }
        
        setChatMessages(messages);
        setActiveView('chat');
      }
    } catch { toast.error('Failed to load analysis'); }
    finally { setLoading(false); }
  };

  // Delete analysis
  const deleteAnalysis = async (analysisId) => {
    try {
      const response = await fetch(`${API_URL}/api/document-intelligence/analysis/${analysisId}`, { method: 'DELETE', headers });
      if (response.ok) {
        toast.success('Analysis deleted');
        fetchHistory();
        if (currentAnalysis?.id === analysisId) {
          setCurrentAnalysis(null); setSessionId(null); setChatMessages([]); setActiveView('upload');
        }
      }
    } catch { toast.error('Failed to delete'); }
  };

  // Clear current session
  const clearSession = () => {
    setFile(null); setTextContent(''); setCustomInstructions('');
    setCurrentAnalysis(null); setSessionId(null); setChatMessages([]);
    setActiveView('upload');
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied'); };

  const getFileIcon = (filename) => {
    if (!filename) return FileText;
    if (filename.endsWith('.csv') || filename.endsWith('.xlsx')) return FileSpreadsheet;
    if (filename.endsWith('.pdf')) return FileType;
    return File;
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6" data-testid="document-intelligence-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-white">Document Intelligence</h1>
              <p className="text-slate-400">Upload documents, give instructions, chat with AI in real time</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* View toggles */}
            <div className="flex bg-slate-900 border border-slate-700 rounded-lg p-1 mr-2">
              <button
                onClick={() => setActiveView('upload')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === 'upload' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'}`}
                data-testid="upload-tab"
              >
                <Upload className="h-4 w-4 inline mr-1.5" />Upload
              </button>
              <button
                onClick={() => currentAnalysis && setActiveView('chat')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === 'chat' ? 'bg-emerald-500/20 text-emerald-400' : currentAnalysis ? 'text-slate-400 hover:text-white' : 'text-slate-600 cursor-not-allowed'}`}
                data-testid="analysis-tab"
              >
                <MessageSquare className="h-4 w-4 inline mr-1.5" />Chat
                {chatMessages.length > 1 && <span className="ml-1 text-xs">({chatMessages.length})</span>}
              </button>
              <button
                onClick={() => setActiveView('history')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === 'history' ? 'bg-purple-500/20 text-purple-400' : 'text-slate-400 hover:text-white'}`}
                data-testid="history-tab"
              >
                <History className="h-4 w-4 inline mr-1.5" />History
              </button>
            </div>

            {currentAnalysis && (
              <>
                <Button onClick={exportCSV} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="export-csv-btn">
                  <Download className="h-4 w-4 mr-1" />CSV
                </Button>
                <Button onClick={exportPDF} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="export-pdf-btn">
                  <FileType className="h-4 w-4 mr-1" />PDF
                </Button>
                <Button onClick={clearSession} variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="new-analysis-btn">
                  <Plus className="h-4 w-4 mr-1" />New
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* ==================== UPLOAD VIEW ==================== */}
        {activeView === 'upload' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {/* File Upload */}
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Upload className="h-5 w-5 text-cyan-400" />
                    Upload Document
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer ${
                      dragActive ? 'border-cyan-500 bg-cyan-500/10' 
                      : file ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50'
                    }`}
                    onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="file-drop-zone"
                  >
                    <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.json" onChange={handleFileSelect} data-testid="file-input" />
                    
                    {file ? (
                      <div className="space-y-3">
                        <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto" />
                        <div>
                          <p className="text-white font-medium">{file.name}</p>
                          <p className="text-sm text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-slate-400 hover:text-red-400">
                          <X className="h-4 w-4 mr-1" />Remove
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Upload className="h-10 w-10 text-slate-500 mx-auto" />
                        <div>
                          <p className="text-white">Drop your document here or click to browse</p>
                          <div className="flex flex-wrap justify-center gap-2 mt-2">
                            {['PDF', 'Word', 'Excel', 'CSV', 'Text'].map(type => (
                              <Badge key={type} variant="outline" className="border-slate-600 text-slate-400">{type}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Paste text toggle */}
                  <div className="mt-3">
                    <Button variant="ghost" size="sm" onClick={() => setShowTextInput(!showTextInput)} className="text-slate-400 hover:text-cyan-400">
                      <Clipboard className="h-4 w-4 mr-2" />{showTextInput ? 'Hide text input' : 'Or paste text content'}
                    </Button>
                    {showTextInput && (
                      <Textarea value={textContent} onChange={(e) => { setTextContent(e.target.value); setFile(null); }}
                        placeholder="Paste your document content here..." className="mt-2 min-h-[150px] bg-slate-800 border-slate-600 text-white" data-testid="text-input" />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Custom Instructions */}
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Wand2 className="h-5 w-5 text-amber-400" />
                    Custom Instructions
                    <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-xs ml-2">Optional</Badge>
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Tell the AI exactly what to focus on, what format you want, or what questions to answer
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder="e.g., Focus only on compliance gaps and map each to the specific CMS CoP section. Ignore financial data. Format as a table with severity, finding, and corrective action columns."
                    className="min-h-[100px] bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="custom-instructions-input"
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[
                      "Focus on compliance gaps only",
                      "Summarize in 5 bullet points",
                      "Format as executive brief",
                      "Compare to industry benchmarks",
                      "Extract all action items"
                    ].map(chip => (
                      <button key={chip} onClick={() => setCustomInstructions(prev => prev ? prev + '. ' + chip : chip)}
                        className="text-xs px-3 py-1.5 rounded-full bg-slate-800 border border-slate-600 text-slate-400 hover:border-amber-500/50 hover:text-amber-400 transition-colors">
                        {chip}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Upload progress + analyze button */}
              {uploading && (
                <div className="space-y-2">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-sm text-slate-400 text-center">{uploadProgress < 100 ? 'Analyzing document...' : 'Processing complete!'}</p>
                </div>
              )}

              <Button onClick={() => analyzeDocument()} disabled={(!file && !textContent.trim()) || uploading}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 h-12 text-base" data-testid="analyze-btn">
                {uploading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Analyzing...</> : <><Sparkles className="h-5 w-5 mr-2" />Analyze & Start Chat</>}
              </Button>
            </div>

            {/* Quick Templates sidebar */}
            <div className="space-y-4">
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Target className="h-5 w-5 text-amber-400" />
                    Quick Templates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  {ANALYSIS_TEMPLATES.map(template => (
                    <Button key={template.id} variant="ghost" className="w-full justify-start text-left hover:bg-slate-800 h-auto py-2.5"
                      onClick={() => analyzeDocument(template.id)} disabled={(!file && !textContent.trim()) || uploading} data-testid={`template-${template.id}`}>
                      <template.icon className={`h-5 w-5 mr-3 flex-shrink-0 ${template.color}`} />
                      <div>
                        <p className="text-white font-medium text-sm">{template.label}</p>
                        <p className="text-xs text-slate-500">{template.description}</p>
                      </div>
                    </Button>
                  ))}
                </CardContent>
              </Card>

              <Alert className="bg-slate-900/80 border-slate-700/50">
                <MessageSquare className="h-4 w-4 text-cyan-400" />
                <AlertTitle className="text-white">Real-Time Chat</AlertTitle>
                <AlertDescription className="text-slate-400 text-sm">
                  After analysis, chat with the AI to drill down, reformat, compare sections, or get specific answers from your document.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        )}

        {/* ==================== CHAT VIEW ==================== */}
        {activeView === 'chat' && currentAnalysis && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ height: 'calc(100vh - 180px)' }}>
            {/* Left: Document Info Panel */}
            <div className="lg:col-span-3 space-y-3 overflow-y-auto">
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    {React.createElement(getFileIcon(currentAnalysis.document_overview?.filename), { className: "h-4 w-4 text-cyan-400" })}
                    Document
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 px-4 pb-4">
                  <div>
                    <p className="text-xs text-slate-500">Filename</p>
                    <p className="text-sm text-white truncate">{currentAnalysis.document_overview?.filename || 'Pasted content'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Type</p>
                    <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">{currentAnalysis.document_overview?.type?.name || 'Unknown'}</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Confidence</p>
                    <Progress value={(currentAnalysis.document_overview?.type?.confidence || 0) * 100} className="h-1.5 mt-1" />
                  </div>
                  {currentAnalysis.document_overview?.dates_found?.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500">Dates Found</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {currentAnalysis.document_overview.dates_found.slice(0, 4).map((date, i) => (
                          <Badge key={i} variant="outline" className="text-xs border-slate-600 text-slate-400">{date}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Instructions */}
              <Card className="bg-slate-900/80 border-slate-700/50">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Zap className="h-4 w-4 text-amber-400" />
                    Quick Instructions
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-1">
                    {getInstructionChips().map((chip, i) => (
                      <button key={i} onClick={() => sendFollowUp(chip)} disabled={chatLoading}
                        className="w-full text-left text-xs px-3 py-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-50"
                        data-testid={`instruction-chip-${i}`}>
                        <ArrowRight className="h-3 w-3 flex-shrink-0" />
                        <span className="line-clamp-2">{chip}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Chat Interface */}
            <div className="lg:col-span-9 flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-xl overflow-hidden">
              {/* Chat header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 bg-slate-900/80">
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-emerald-400" />
                  <span className="text-white font-semibold">Document Chat</span>
                  <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-xs">AI-Powered</Badge>
                </div>
                <span className="text-xs text-slate-500">{chatMessages.length} messages</span>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 px-5 py-4" data-testid="chat-messages">
                <div className="space-y-4 max-w-4xl mx-auto">
                  {chatMessages.map((message, index) => (
                    <div key={index} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`} data-testid={`chat-message-${index}`}>
                      {message.role === 'assistant' && (
                        <div className="p-2 rounded-lg bg-emerald-500/20 h-fit flex-shrink-0 mt-1">
                          <Bot className="h-4 w-4 text-emerald-400" />
                        </div>
                      )}
                      <div className={`rounded-xl p-4 ${
                        message.role === 'user'
                          ? message.isInstruction
                            ? 'bg-amber-500/10 border border-amber-500/20 max-w-[85%]'
                            : 'bg-cyan-500/15 border border-cyan-500/20 max-w-[85%]'
                          : 'bg-slate-800/80 border border-slate-700/50 max-w-[90%]'
                      }`}>
                        {message.isInstruction && (
                          <div className="flex items-center gap-1.5 mb-2">
                            <Wand2 className="h-3 w-3 text-amber-400" />
                            <span className="text-xs text-amber-400 font-medium">Custom Instructions</span>
                          </div>
                        )}
                        <div className="text-sm">
                          {message.role === 'assistant' ? renderMarkdown(message.content) : (
                            <p className="text-slate-200 whitespace-pre-wrap">{message.content}</p>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-700/30">
                          <span className="text-xs text-slate-500">{new Date(message.timestamp).toLocaleTimeString()}</span>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(message.content)} className="h-6 px-2 text-slate-500 hover:text-slate-300">
                            <Clipboard className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {message.role === 'user' && !message.isInstruction && (
                        <div className="p-2 rounded-lg bg-cyan-500/20 h-fit flex-shrink-0 mt-1">
                          <User className="h-4 w-4 text-cyan-400" />
                        </div>
                      )}
                      {message.isInstruction && (
                        <div className="p-2 rounded-lg bg-amber-500/20 h-fit flex-shrink-0 mt-1">
                          <Wand2 className="h-4 w-4 text-amber-400" />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {chatLoading && (
                    <div className="flex gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <Bot className="h-4 w-4 text-emerald-400" />
                      </div>
                      <div className="bg-slate-800/80 rounded-xl px-5 py-3 border border-slate-700/50">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                          <span className="text-sm text-slate-400">Analyzing...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={chatEndRef} />
                </div>
              </ScrollArea>

              {/* Chat input area */}
              <div className="border-t border-slate-700 bg-slate-900/80 px-5 py-4">
                <div className="flex gap-3 max-w-4xl mx-auto">
                  <Input
                    ref={chatInputRef}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendFollowUp()}
                    placeholder="Give the AI specific instructions about this document..."
                    className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 h-11"
                    disabled={chatLoading}
                    data-testid="chat-input"
                  />
                  <Button onClick={() => sendFollowUp()} disabled={!chatInput.trim() || chatLoading}
                    className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 h-11 px-5" data-testid="send-btn">
                    {chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== HISTORY VIEW ==================== */}
        {activeView === 'history' && (
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-white">
                  <History className="h-5 w-5 text-purple-400" />
                  Analysis History
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchHistory} className="text-slate-400 hover:text-white">
                  <RefreshCw className={`h-4 w-4 ${historyLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                </div>
              ) : analysisHistory.length > 0 ? (
                <div className="space-y-3">
                  {analysisHistory.map((analysis) => (
                    <div key={analysis.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-purple-500/50 transition-colors">
                      <div className="flex items-center gap-4">
                        {React.createElement(getFileIcon(analysis.document_overview?.filename), { className: "h-8 w-8 text-slate-500" })}
                        <div>
                          <p className="text-white font-medium">{analysis.document_overview?.filename || 'Pasted content'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">{analysis.document_overview?.type?.name || 'Document'}</Badge>
                            <span className="text-xs text-slate-500">{new Date(analysis.timestamp).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => loadAnalysis(analysis.id)}
                          className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10" data-testid={`open-analysis-${analysis.id}`}>
                          <MessageSquare className="h-4 w-4 mr-1" />Chat
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteAnalysis(analysis.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No analysis history yet</p>
                  <p className="text-sm mt-1">Upload a document to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
